import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_URL } from "../../config.js";
import ConfirmDialog from "../../components/ConfirmDialog.js";
import Notification from "../../components/Notification.js";
import { allowedToView } from "../../utils/PermissionUtils.js";

function Farmers() {
    const [farmers, setFarmers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [selectedFarmer, setSelectedFarmer] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const perPage = 20; // Items per page

    const userRole = localStorage.getItem("userRole");
    const canEdit = allowedToView(userRole, ['admin', 'superadmin', 'user']);
    const navigate = useNavigate();

    const fetchFarmers = useCallback(async () => {
        const token = localStorage.getItem("access_token");
        try {
            setLoading(true);

            // Construct the URL with the search parameter only if searchTerm is not empty
            const url = new URL(`${API_URL}/farmers`);
            url.searchParams.append("page", currentPage);
            if (searchTerm.trim()) {
                url.searchParams.append("search", searchTerm.trim());
            }

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const responseData = await response.json();
                console.log("Fetched Response:", responseData);

                if (responseData.data) {
                    setFarmers(responseData.data);
                    setTotalPages(responseData.last_page || 1);
                } else {
                    setFarmers([]);
                    setTotalPages(1);
                }
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                setError("Failed to fetch farmers");
            }
        } catch (error) {
            setError("An error occurred while fetching farmers.");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, navigate]);

    useEffect(() => {
        fetchFarmers();
    }, [currentPage, searchTerm, fetchFarmers]);

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    return (
        <div>
            <main>
                <nav className="level">
                    <div className="level-left">
                        <p className="title">Farmers</p>
                    </div>
                    <div className="level-right">
                        {canEdit && (
                            <Link to="add" className="button primary-bg">
                                <i className="fa fa-plus"></i> &nbsp; New
                            </Link>
                        )}
                    </div>
                </nav>

                <h1>Farmers List</h1>

                {notification.message && (
                    <Notification type={notification.type} message={notification.message} />
                )}

                <input
                    className="input"
                    type="text"
                    placeholder="Search farmers..."
                    value={searchTerm}
                    onChange={handleSearch}
                />

                {loading ? (
                    <p>Loading farmers...</p>
                ) : error ? (
                    <p className="has-text-danger">{error}</p>
                ) : (
                    <table className="table is-striped is-fullwidth">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Number</th>
                                <th>ID</th>
                                <th>Phone</th>
                                <th>Route</th>
                                <th>Options</th>
                            </tr>
                        </thead>
                        <tbody>
                            {farmers?.length > 0 ? (
                                farmers.map((farmer, index) => (
                                    <tr key={farmer.id}>
                                        <td>{index + 1 + (currentPage - 1) * perPage}</td>
                                        <td>
                                            <Link to={`${farmer.id}/details`} className="no_deco_link">
                                                {farmer.first_name} {farmer.middle_name} {farmer.last_name}
                                            </Link>
                                        </td>
                                        <td>{farmer.farmer_number || "N/A"}</td>
                                        <td>{farmer.id_number || "N/A"}</td>
                                        <td>{farmer.phone_number || "N/A"}</td>
                                        <td>{farmer.collection_route?.name || "N/A"}</td>
                                        <td>
                                            <Link to={`${farmer.id}/details`} className="no_deco_link"> <span className="icon">
                                                <i className="fas fa-eye"></i>
                                            </span>
                                                <span>View</span>
                                            </Link>
                                            &nbsp;
                                            {canEdit && (
                                                <Link to={`${farmer.id}/update`} className="no_deco_link">
                                                    <span className="icon">
                                                        <i className="fas fa-edit"></i>
                                                    </span>
                                                    <span> Edit</span>
                                                </Link>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="has-text-centered">No farmers found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}

                {/* Pagination Controls */}
                <nav className="pagination is-centered">
                    <button
                        className="pagination-previous"
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                    <span className="pagination-page">Page {currentPage} of {totalPages}</span>
                    <button
                        className="pagination-next"
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </nav>
            </main>
        </div>
    );
}

export default Farmers;